import './histogram.scss';
import {memo, useMemo, useState} from 'react';
import {BarChart} from './barChart';
import {Handles, Rail, Slider, Tracks} from 'react-compound-slider';
import {Handle, SliderRail, Track} from './components';
import {formatDateShort} from '../utils/utils';

export type HistogramSnapPoint = {
    value: string;
    count: number;
};

type HistogramProps = {
    snapPoints: HistogramSnapPoint[];
    range: number[];
    selection?: number[];
    handleSelection: (selection: number[]) => void;
};

export const Histogram = memo(({range, selection, handleSelection, snapPoints}: HistogramProps) => {
    const [values, setValues] = useState<number[]>(selection || range);
    // Values will be updated more frequently.
    // Therefore, we'll use a separate state to prevent histogram from re-rendering too much
    const [highlight, setChartHighlights] = useState<number[]>(selection || range);

    useMemo(() => {
        setValues(selection || range);
        setChartHighlights(selection || range);
    }, [range, selection]);

    return (
        <div className="histogram-wrapper">
            <div className="histogram">
                <BarChart data={snapPoints} highlight={highlight} domain={range}/>
                <HistogramSlider
                    range={range}
                    handleSelection={handleSelection}
                    setValues={setValues}
                    setChartHighlights={setChartHighlights}
                    values={values}
                />
            </div>
            <HistogramSelection selection={values} />
        </div>
    );
});

interface HistogramSliderProps {
    range: number[];
    handleSelection: (selection: number[]) => void;
    setValues: (v: number[]) => void;
    setChartHighlights: (v: number[]) => void;
    values: number[];
}

export const HistogramSlider = memo(
    ({range, handleSelection, setValues, setChartHighlights, values}: HistogramSliderProps) => {
        return (
            <Slider
                mode={2}
                step={1}
                domain={range}
                className={'date-slider'}
                onSlideEnd={(v) => handleSelection([...v])}
                onUpdate={(values) => setValues([...values])}
                onChange={(v) => setChartHighlights([...v])}
                values={values}
            >
                <Rail>{({getRailProps}) => <SliderRail getRailProps={getRailProps} />}</Rail>
                <Handles>
                    {({handles, getHandleProps}) => (
                        <div className="slider-handles">
                            {handles.map((handle) => (
                                <Handle
                                    key={handle.id}
                                    handle={handle}
                                    domain={range}
                                    getHandleProps={getHandleProps}
                                />
                            ))}
                        </div>
                    )}
                </Handles>
                <Tracks left={false} right={false}>
                    {({tracks, getTrackProps}) => (
                        <div className="slider-tracks">
                            {tracks.map(({id, source, target}) => (
                                <Track key={id} source={source} target={target} getTrackProps={getTrackProps} />
                            ))}
                        </div>
                    )}
                </Tracks>
            </Slider>
        );
    }
);

interface HistogramSelectionProps {
    selection: number[];
}

export const HistogramSelection = memo(({selection}: HistogramSelectionProps) => {
    return (
        <div className="histogram-selection">
            <span className="selection">{formatDateShort(selection[0])}</span>
            <span className="selection-spacer">&mdash;</span>
            <span className="selection">{formatDateShort(selection[1])}</span>
        </div>
    );
});
